import { Controller } from 'stimulus'

/**
 * Used for "multistep" lead form (/apply/lead)
 *
 * @class LeadFormController
 * @memberof Controllers
 *
 */
export default class extends Controller {
  static currentStep
  static targets = [
    'step',
    'firstStep',
    'secondStep',
    'thankYouStep',
    'endorser',
    'originalEndorser',
    'source',
    'originalSource',
    'internetSource',
    'dmLead',
    'stateSelect',
    'firstName',
    'lastName',
    'email',
    'phone',
    'noRedirect',
    'submitButton'
  ]

  /**
   * @function Controllers.LeadFormController.connect
   * @memberof Controllers
   * @description On connection show first step of the flow
   */
  connect() {
    this._showStep(this.firstStepTarget)
    this._sendGTMDataLayer('firstStep')
  }

  /**
   * @function Controllers.LeadFormController.next
   * @memberof Controllers
   * @description Go to the second step in the form
   */
  next() {
    event.preventDefault()

    if (this._stepOneValid) {
      this._showStep(this.secondStepTarget)
      this.currentStep = this.secondStepTarget
      this._sendGTMDataLayer('secondStep')
      document.getElementById('lead_first_name').focus()
    }
  }

  /**
   * @function Controllers.LeadFormController.submit
   * @memberof Controllers
   * @description Submit the lead, show the Thank You step
   */
  async submit() {
    event.preventDefault()
    this._loadBouncer()
    const isDmLead = this.dmLeadTarget.value

    if (this._stepTwoValid) {
      const result = await this._submitForm()
      if (isDmLead && result.includes('floify.com/verify')) {
        window.location.href = result
      } else {
        this._showStep(this.thankYouStepTarget)
        this._sendGTMDataLayer('thankYouStep')
        this.currentStep = this.thankYouStepTarget
      }
    }
  }

  /**
   * @function Controllers.LeadFormController.navBack
   * @memberof Controllers
   * @description Shows previous step if possible
   * @param {Event} event
   */
  navBack(event) {
    event.preventDefault()

    if (this.currentStep === this.secondStepTarget) {
      this._showStep(this.firstStepTarget)
    }
  }

  /**
   * @function Controllers.LeadFormController._stepOneValid
   * @memberof Controllers
   * @description Validates if the first step is valid
   * @private
   * @return {boolean}
   */
  get _stepOneValid() {
    const fields = [
      document.getElementById('lead_state'),
      document.getElementById('lead_type_purchase'),
      document.getElementById('lead_type_refinance')
    ]

    return this._validationOf(fields)
  }

  /**
   * @function Controllers.LeadFormController._stepTwoValid
   * @memberof Controllers
   * @description Validates if the second step is valid
   * @private
   * @return {boolean}
   */
  get _stepTwoValid() {
    const fields = [
      document.getElementById('lead_first_name'),
      document.getElementById('lead_last_name'),
      document.getElementById('lead_email'),
      document.getElementById('lead_phone')
    ]

    return this._validationOf(fields)
  }

  /**
   * @function Controllers.LeadFormController._validationOf
   * @memberof Controllers
   * @description Validates all provided input fields
   * @param {Array} fields to validate
   * @private
   * @return {boolean}
   */
  _validationOf(fields) {
    const validity = fields.map(field => field.checkValidity())

    this.element.reportValidity()

    return validity.every(field => field === true)
  }

  /**
   * @function Controllers.LeadFormController._fetchSubmitForm
   * @memberof Controllers
   * @description Submits the form via fetch API
   * @private
   */
  async _submitForm() {
    const lead = this._formValues

    const response = await fetch('/leads', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify({ lead, no_redirect: this.noRedirectTarget.value })
    })
    const result = await response.json()

    return result.redirect_url
  }

  /**
   * @function Controllers.LeadFormController._showStep
   * @memberof Controllers
   * @description Shows the passed in step
   * @param {target} step to show
   * @private
   */
  _showStep(step) {
    this._hideAllSteps()
    step.classList.add(this._activeStepCssClass)
    this.currentStep = step
    window.scrollTo({ top: 0 })

    // make sure we don't trap people in the lead form
    if (step !== this.firstStepTarget) {
      window.history.pushState({}, null, null)
    }
  }

  /**
   * @function Controllers.LeadFormController._hideAllSteps
   * @memberof Controllers
   * @description Hides all steps
   * @private
   */
  _hideAllSteps() {
    this.stepTargets.forEach(step => {
      step.classList.remove(this._activeStepCssClass)
    })
  }

  /**
   * @function Controllers.LeadFormController._sendGTMDataLayer
   * @memberof Controllers
   * @description Send data to GTM for reporting
   * @param {string} step description
   * @private
   */
  _sendGTMDataLayer(step) {
    window.dataLayer = window.dataLayer || []
    const applicationStart = (step === 'firstStep').toString()
    const applicationComplete = (step === 'thankYouStep').toString()

    window.dataLayer.push({
      'application_start':    applicationStart,
      'application_complete': applicationComplete,
      'current_page':         step,
      'session_uuid':         sessionStorage.getItem('sessionUUID'),
      'event':                'turboPageLoad',
      'product_type':         this._leadType,
      'form_type':            '2-step'
    })
  }

  /**
   * @function Controllers.LeadFormController._loadBouncer
   * @memberof Controllers
   * @description Load bouncing loader element
   * @private
   */
  _loadBouncer() {
    this.submitButtonTarget.innerHTML = `
      <div class="bouncing-loader">
        <div></div>
        <div></div>
        <div></div>
      </div>
    `
  }

  /**
   * @function Controllers.LeadFormController._activeStepCssClass
   * @memberof Controllers
   * @description Gets the CSS Class Name used for showing steps
   * @private
   * @return {string}
   */
  get _activeStepCssClass() {
    return this.element.dataset.leadFormActiveClassValue
  }

  /**
   * @function Controllers.LeadFormController._isPurchaseLead
   * @memberof Controllers
   * @description Gets whether or not the current lead is a purchase
   * @private
   * @return {boolean}
   */
  get _isPurchaseLead() {
    return document.getElementById('lead_type_purchase').checked
  }

  /**
   * @function Controllers.LeadFormController._isRefinanceLead
   * @memberof Controllers
   * @description Gets whether or not the current lead is a refinance
   * @private
   * @return {boolean}
   */
  get _isRefinanceLead() {
    return document.getElementById('lead_type_refinance').checked
  }

  /**
   * @function Controllers.LeadFormController._leadType
   * @memberof Controllers
   * @description Gets current lead type, 'purchase', 'refinance' or 'tbd'
   * @private
   * @return {string}
   */
  get _leadType() {
    if (this._isPurchaseLead === true) {
      return 'purchase'
    } else if (this._isRefinanceLead === true) {
      return 'refinance'
    } else {
      return 'tbd'
    }
  }

  /**
   * @function Controllers.LeadFormController._formValues
   * @memberof Controllers
   * @description Gathers all form values into an object
   * @private
   * @return {Object}
   */
  get _formValues() {
    const dmLead = this.dmLeadTarget.value
    const endorser = this.endorserTarget.value
    const source = this.sourceTarget.value
    const internetSource = this.internetSourceTarget.value
    const originalEndorser = this.originalEndorserTarget.value
    const originalSource = this.originalSourceTarget.value
    const state = this.stateSelectTarget.value
    const firstName = this.firstNameTarget.value
    const lastName = this.lastNameTarget.value
    const email = this.emailTarget.value
    const phone = this.phoneTarget.value
    const type = this._leadType

    return {
      dm_lead:           dmLead,
      endorser,
      source,
      internet_source:   internetSource,
      original_endorser: originalEndorser,
      original_source:   originalSource,
      state,
      first_name:        firstName,
      last_name:         lastName,
      email,
      phone,
      type
    }
  }
}
